import React, { useEffect, useMemo, useState } from "react";
import { toResult, type Result } from "@sequesign/verifier-browser";

import { SCENARIO, type ScenarioLayer } from "./scenario/torbridge.js";
import { BUNDLES, POLICY_CHECKED_SCHEMA } from "./data.js";
import { bundleToLoadedReceipt } from "./load/bundle.js";
import { runDemoVerification } from "./verify/run-demo.js";
import { runSerialized } from "./verify/serialize.js";
import { breakLayer, makeSchemaOverride, type BreakKind } from "./break.js";
import { DisclosureBanner } from "./ui/DisclosureBanner.js";
import { Footer } from "./ui/Footer.js";
import { AgentScreen } from "./ui/AgentScreen.js";
import { LayerSelector } from "./ui/LayerSelector.js";
import { VerificationPanel } from "./ui/VerificationPanel.js";
import { SchemaView, TemplateView, ReceiptView } from "./ui/ArtifactViews.js";
import { SchemaRefusalDemo } from "./ui/SchemaRefusalDemo.js";
import { LivePanel } from "./ui/LivePanel.js";
import { HeroPanel } from "./ui/HeroPanel.js";

type Mode = "hero" | "recorded" | "live";

export function App() {
  const [mode, setMode] = useState<Mode>("hero");
  const [selectedId, setSelectedId] = useState<string>("L0");
  const [activeBreak, setActiveBreak] = useState<BreakKind | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const layer = useMemo<ScenarioLayer>(
    () => SCENARIO.layers.find((l) => l.id === selectedId) ?? SCENARIO.layers[0],
    [selectedId]
  );

  // The current layer's recorded bundle (for the artifact views). null on L0.
  const bundle = useMemo(
    () => (layer.layerDataId ? BUNDLES[layer.layerDataId] : null),
    [layer.layerDataId]
  );

  // Reset the break whenever the selected layer changes.
  function selectLayer(id: string) {
    setSelectedId(id);
    setActiveBreak(null);
  }

  useEffect(() => {
    let cancelled = false;

    // Only recorded mode runs the layered verification below; the hero and
    // live views render their own panels.
    if (mode !== "recorded") {
      return;
    }

    if (!layer.layerDataId) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }

    const bundle = BUNDLES[layer.layerDataId];
    if (!bundle) {
      setError(`No recorded bundle for ${layer.layerDataId}`);
      setResult(null);
      return;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        let files = new Map(Object.entries(bundle));
        // "tamper-schema" does NOT edit the package — it substitutes the schema
        // the verifier loads. Every other break mutates the file map.
        const schemaOverride =
          activeBreak === "tamper-schema" ? makeSchemaOverride(POLICY_CHECKED_SCHEMA) : undefined;
        if (activeBreak && activeBreak !== "tamper-schema") {
          files = breakLayer(files, activeBreak);
        }
        const loaded = bundleToLoadedReceipt(layer.layerDataId!, Object.fromEntries(files));
        const started = performance.now();
        // Serialized process-wide: the browser verifier shares one virtual FS,
        // so this must not overlap a hero-card verification after a mode switch.
        const { report } = await runSerialized(() =>
          runDemoVerification(loaded, { schemaOverride })
        );
        const totalMs = performance.now() - started;
        if (cancelled) return;
        setResult(toResult(report, { totalMs, verifierVersion: "demo", commit: "demo" }));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setResult(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [layer.layerDataId, activeBreak, mode]);

  const isL0 = layer.layerDataId === null;

  return (
    <div className="page">
      <DisclosureBanner />

      <header className="masthead">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            T
          </span>
          <div>
            <div className="brand-name">{SCENARIO.client.name}</div>
            <div className="brand-domain">{SCENARIO.client.domain}</div>
          </div>
        </div>
        <div className="masthead-tag">Agent Activity, Verified</div>
      </header>

      <div className="mode-toggle" role="tablist" aria-label="Demo section">
        <button
          role="tab"
          aria-selected={mode === "hero"}
          className={"mode-toggle-btn" + (mode === "hero" ? " mode-toggle-btn-active" : "")}
          onClick={() => setMode("hero")}
        >
          Overview
        </button>
        <button
          role="tab"
          aria-selected={mode === "recorded"}
          className={"mode-toggle-btn" + (mode === "recorded" ? " mode-toggle-btn-active" : "")}
          onClick={() => setMode("recorded")}
        >
          Trust layers
        </button>
        <button
          role="tab"
          aria-selected={mode === "live"}
          className={"mode-toggle-btn" + (mode === "live" ? " mode-toggle-btn-active" : "")}
          onClick={() => setMode("live")}
        >
          Run live
        </button>
      </div>

      {mode === "hero" ? (
        <main className="content">
          <HeroPanel
            hero={SCENARIO.hero}
            flow={SCENARIO.flow}
            bundles={BUNDLES}
            ctas={[
              { ...SCENARIO.hero.layersCta, onClick: () => setMode("recorded") },
              { ...SCENARIO.hero.liveCta, onClick: () => setMode("live") }
            ]}
          />
        </main>
      ) : mode === "live" ? (
        <main className="content">
          <LivePanel />
        </main>
      ) : (
        <main className="content">
          <section className="intro">
            <h1>Every agent action, provable.</h1>
            <p>
              Our accounts-payable agent reviewed vendor invoice{" "}
              <strong>{SCENARIO.task.invoiceId}</strong> under a{" "}
              {SCENARIO.task.authority.toLowerCase()} {SCENARIO.task.conformantOutcome} Step through
              the trust layers below to see what each one lets an outside party check, and try to
              break one.
            </p>
          </section>

          <LayerSelector selectedId={selectedId} onSelect={selectLayer} />

          <section className="layer-body">
            <div className="layer-explainer">
              <div className="layer-explainer-badge">{layer.badge}</div>
              <h2>{layer.title}</h2>
              <p>{layer.blurb}</p>
              <div className="layer-explainer-teach">
                {layer.explainer.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
              {selectedId === "L3!" && (
                <p className="layer-explainer-extra">{SCENARIO.task.violationOutcome}</p>
              )}
            </div>

            {isL0 ? (
              <AgentScreen />
            ) : (
              <div className="layer-main">
                <VerificationPanel
                  result={result}
                  loading={loading}
                  error={error}
                  breaks={layer.breaks}
                  activeBreak={activeBreak}
                  onBreak={(k) => setActiveBreak(k)}
                  onRestore={() => setActiveBreak(null)}
                />

                {selectedId === "L2" && <SchemaRefusalDemo />}
                {selectedId === "L2" && <SchemaView />}
                {(selectedId === "L3" || selectedId === "L3!") && bundle && (
                  <TemplateView
                    profileJson={bundle["profile.json"]}
                    paramsJson={bundle["params.json"]}
                  />
                )}
                {bundle && bundle["receipt.json"] && (
                  <ReceiptView
                    receiptJson={bundle["receipt.json"]}
                    defaultOpen={selectedId === "L1"}
                  />
                )}
              </div>
            )}
          </section>
        </main>
      )}

      <Footer />
    </div>
  );
}
